import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Pencil, Trash2, Store, AlertTriangle, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import ColorPicker from '@/components/ColorPicker';
import { HelpTip } from '@/components/HelpTip';

const PLACEHOLDER_RULES = `Exemplo de regras:

Valor do item até R$79,99
20% + R$4

Valor entre R$80 e R$199,99
14% + R$16

Acima de R$200
14% + R$26

Programa Frete Grátis
+6%`;

const formatBRL = (v) => {
  if (v == null) return 'R$0,00';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const RulesSummary = ({ parsedRules }) => {
  if (!parsedRules || parsedRules.length === 0) {
    return <span className="text-xs text-muted-foreground italic">Nenhuma regra configurada</span>;
  }
  const faixas = parsedRules.filter(r => r.tipo === 'faixa');
  const adicionais = parsedRules.filter(r => r.tipo === 'adicional');
  const parts = [];
  if (faixas.length > 0) parts.push(`${faixas.length} faixa${faixas.length > 1 ? 's' : ''}`);
  if (adicionais.length > 0) parts.push(`${adicionais.length} regra${adicionais.length > 1 ? 's' : ''} geral`);
  return (
    <div className="text-xs text-muted-foreground space-y-0.5" data-testid="rules-summary">
      <span className="font-medium">{parts.join(' + ')}</span>
    </div>
  );
};

const ParsedRulesPreview = ({ rules, warnings }) => {
  if ((!rules || rules.length === 0) && (!warnings || warnings.length === 0)) return null;

  return (
    <div className="mt-3 space-y-2" data-testid="rules-preview">
      {rules && rules.length > 0 && (
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <CheckCircle2 size={14} className="text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
              {rules.length} regra{rules.length > 1 ? 's' : ''} interpretada{rules.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-1.5">
            {rules.map((rule, i) => (
              <div key={i} className="text-xs flex items-start gap-2 text-foreground">
                <span className={`px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                  rule.tipo === 'faixa'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400'
                }`}>
                  {rule.tipo === 'faixa' ? 'Faixa' : 'Geral'}
                </span>
                <span className="flex-1">
                  {rule.nome}
                  <span className="text-muted-foreground ml-1">
                    — {rule.comissao_percent}%
                    {rule.taxa_fixa > 0 && ` + ${formatBRL(rule.taxa_fixa)}`}
                    {rule.tipo === 'faixa' && rule.max_valor != null && (
                      <span className="ml-1 opacity-70">
                        ({formatBRL(rule.min_valor || 0)} ~ {formatBRL(rule.max_valor)})
                      </span>
                    )}
                    {rule.tipo === 'faixa' && rule.max_valor == null && (
                      <span className="ml-1 opacity-70">
                        (acima de {formatBRL(rule.min_valor || 0)})
                      </span>
                    )}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {warnings && warnings.length > 0 && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <AlertTriangle size={14} className="text-amber-600" />
            <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">Avisos</span>
          </div>
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-700 dark:text-amber-400">{w}</p>
          ))}
        </div>
      )}
    </div>
  );
};

const Marketplaces = () => {
  const [marketplaces, setMarketplaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMarketplace, setEditingMarketplace] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '#F97316', regras_taxas: '' });
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);

  const loadMarketplaces = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/marketplaces');
      setMarketplaces(response.data);
    } catch (error) {
      toast.error('Erro ao carregar marketplaces');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMarketplaces(); }, [loadMarketplaces]);

  const handleValidate = async () => {
    if (!formData.regras_taxas.trim()) {
      setValidationResult({ rules: [], warnings: [] });
      return;
    }
    setValidating(true);
    try {
      const res = await api.post('/marketplaces/validate-rules', { regras_taxas: formData.regras_taxas });
      setValidationResult(res.data);
    } catch {
      toast.error('Erro ao validar regras');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        color: formData.color,
        regras_taxas: formData.regras_taxas,
      };
      if (editingMarketplace) {
        await api.put(`/marketplaces/${editingMarketplace.id}`, payload);
        toast.success('Marketplace atualizado!');
      } else {
        await api.post('/marketplaces', payload);
        toast.success('Marketplace criado!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadMarketplaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar marketplace');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir este marketplace?')) return;
    try {
      await api.delete(`/marketplaces/${id}`);
      toast.success('Marketplace excluído!');
      loadMarketplaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir marketplace');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', color: '#F97316', regras_taxas: '' });
    setEditingMarketplace(null);
    setValidationResult(null);
  };

  const openEditDialog = (mp) => {
    setEditingMarketplace(mp);
    setFormData({
      name: mp.name,
      color: mp.color,
      regras_taxas: mp.regras_taxas || '',
    });
    setValidationResult(
      mp.regras_parsed && mp.regras_parsed.length > 0
        ? { rules: mp.regras_parsed, warnings: [] }
        : null
    );
    setIsDialogOpen(true);
  };

  return (
    <div data-testid="marketplaces-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Marketplaces
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Gerencie os marketplaces e suas regras de taxas
          <HelpTip text="Cadastre aqui os marketplaces onde você vende (Shopee, TikTok, etc). Defina as regras de taxas de cada um para que o sistema calcule automaticamente seus lucros." className="ml-1" />
        </p>
      </div>

      <div className="mb-6">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-marketplace-button" className="bg-indigo-600 hover:bg-indigo-700">
              <Plus size={18} className="mr-2" />
              Novo Marketplace
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="marketplace-dialog">
            <DialogHeader>
              <DialogTitle>{editingMarketplace ? 'Editar' : 'Novo'} Marketplace</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input
                  data-testid="marketplace-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Shopee, Mercado Livre"
                />
              </div>
              <div>
                <Label>Cor</Label>
                <ColorPicker value={formData.color} onChange={(c) => setFormData({ ...formData, color: c })} testIdPrefix="mp-color" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="flex items-center gap-1.5">
                    <FileText size={14} />
                    Regras de Taxas
                    <HelpTip text="Cole aqui as regras de comissão do marketplace. O sistema interpreta automaticamente faixas de preço, porcentagens e taxas fixas. Aceita qualquer formato de texto." />
                  </Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleValidate}
                    disabled={validating}
                    data-testid="validate-rules-button"
                    className="h-7 text-xs"
                  >
                    {validating ? 'Validando...' : 'Validar Regras'}
                  </Button>
                </div>
                <textarea
                  data-testid="marketplace-rules-textarea"
                  value={formData.regras_taxas}
                  onChange={(e) => {
                    setFormData({ ...formData, regras_taxas: e.target.value });
                    setValidationResult(null);
                  }}
                  placeholder={PLACEHOLDER_RULES}
                  rows={8}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y min-h-[120px]"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Escreva ou cole as regras do marketplace. O sistema interpreta automaticamente faixas de preço, comissões e taxas.
                </p>
                <ParsedRulesPreview
                  rules={validationResult?.rules}
                  warnings={validationResult?.warnings}
                />
              </div>
              <Button type="submit" data-testid="marketplace-submit-button" className="w-full bg-indigo-600 hover:bg-indigo-700">
                {editingMarketplace ? 'Atualizar' : 'Criar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="marketplaces-list">
        {loading ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">Carregando...</div>
        ) : marketplaces.length === 0 ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">Nenhum marketplace cadastrado</div>
        ) : (
          marketplaces.map((mp) => (
            <Card key={mp.id} className="hover:shadow-md transition-shadow overflow-hidden" data-testid={`marketplace-card-${mp.id}`}>
              <div className="h-2 w-full" style={{ backgroundColor: mp.color }} />
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: mp.color }}>
                    <Store size={18} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">{mp.name}</CardTitle>
                    <RulesSummary parsedRules={mp.regras_parsed} />
                  </div>
                </div>
              </CardHeader>
              {mp.regras_parsed && mp.regras_parsed.length > 0 && (
                <CardContent className="pt-0 pb-3">
                  <div className="space-y-1">
                    {mp.regras_parsed.slice(0, 4).map((rule, i) => (
                      <div key={i} className="text-xs flex items-center gap-1.5 text-muted-foreground">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          rule.tipo === 'faixa' ? 'bg-blue-500' : 'bg-violet-500'
                        }`} />
                        <span className="truncate">
                          {rule.nome}: {rule.comissao_percent}%
                          {rule.taxa_fixa > 0 && ` + ${formatBRL(rule.taxa_fixa)}`}
                        </span>
                      </div>
                    ))}
                    {mp.regras_parsed.length > 4 && (
                      <p className="text-xs text-muted-foreground/70 pl-3">+{mp.regras_parsed.length - 4} mais...</p>
                    )}
                  </div>
                </CardContent>
              )}
              <CardContent className={mp.regras_parsed && mp.regras_parsed.length > 0 ? 'pt-0' : ''}>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEditDialog(mp)} className="flex-1">
                    <Pencil size={14} className="mr-1.5" />Editar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(mp.id)}
                    className="flex-1 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200">
                    <Trash2 size={14} className="mr-1.5" />Excluir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Marketplaces;
